import { useMemo, useRef, useState } from 'react';
import { Checkbox } from './Checkbox';

// TeamTree — story 2-146d (P10 §2.2/§2.4). The org/role hierarchy primitive:
// "nested checkbox tree (region→branch→team), indeterminate parents,
// keyboard navigable" — extended here for long organization names per
// 2-137's content rules (truncate + `min-width: 0` on the text-bearing flex
// child, full name recoverable via `title`, never clipped without a way to
// read it).
//
// SHAPE. The org/access API (`lib/api/access.gen.ts`'s `Team`) is a FLAT
// list — `{ id, name, kind, parentTeamId, depth }` — already returned in
// tree order (`OrgAccessPage`'s own comment). TeamTree builds the nested
// structure from that flat shape itself, so a consumer never hand-rolls a
// tree just to hand it back in.
//
// SELECTION MODEL. `value` is the set of CHECKED LEAF ids (a leaf = a node
// with no children in the given `nodes`); every ancestor's checked/
// indeterminate state is fully DERIVED, never stored, so there is no way for
// a parent's displayed state to drift from its children's. Checking a
// non-leaf node cascades to every descendant leaf (the familiar
// folder-tree checkbox behaviour); checking a leaf toggles just that id.
// `selectionMode="single"` (the org/access console's own parent-team picker)
// short-circuits the cascade: checking any node replaces the whole
// selection with just that one id, so at most one id is ever checked and no
// parent ever renders indeterminate — still the same checkbox-tree
// component, just exercised with a single pick. The general multi-select /
// indeterminate-parent contract is proven in the gallery below, where a
// realistic region→branch→team fixture selects multiple teams under one
// branch on purpose.
//
// KEYBOARD (P10 §2.3): every row is a real, always-visible `<input
// type="checkbox">` (Tab reaches every node — no collapse/expand to fold
// content out of the tab order in the first place, so there is nothing to
// keep in sync the way OrderedListField's focus-follows-the-move logic
// does), plus ArrowUp/ArrowDown/Home/End move a roving-tabindex "active" row
// across the whole flattened tree order, matching the WAI-ARIA treeview
// pattern without inventing a second navigation model.

export interface TeamTreeNode {
  id: string;
  name: string;
  kind: string;
  parentTeamId: string | null;
}

export interface TeamTreeProps {
  'aria-label': string;
  nodes: readonly TeamTreeNode[];
  value: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  selectionMode?: 'multiple' | 'single';
  disabled?: boolean;
  emptyMessage?: string;
  className?: string;
  'data-testid'?: string;
}

interface BuiltNode extends TeamTreeNode {
  children: BuiltNode[];
  depth: number;
}

function buildTree(nodes: readonly TeamTreeNode[]): BuiltNode[] {
  const byId = new Map<string, BuiltNode>(nodes.map((n) => [n.id, { ...n, children: [], depth: 0 }]));
  const roots: BuiltNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentTeamId ? byId.get(node.parentTeamId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const assignDepth = (list: BuiltNode[], depth: number) => {
    for (const n of list) {
      n.depth = depth;
      assignDepth(n.children, depth + 1);
    }
  };
  assignDepth(roots, 0);
  return roots;
}

function flatten(list: BuiltNode[]): BuiltNode[] {
  const out: BuiltNode[] = [];
  const walk = (nodes: BuiltNode[]) => {
    for (const n of nodes) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(list);
  return out;
}

function leafIds(node: BuiltNode): string[] {
  if (node.children.length === 0) return [node.id];
  return node.children.flatMap(leafIds);
}

function stateOf(node: BuiltNode, value: ReadonlySet<string>): 'checked' | 'indeterminate' | 'unchecked' {
  const leaves = leafIds(node);
  const checkedCount = leaves.filter((id) => value.has(id)).length;
  if (checkedCount === 0) return 'unchecked';
  if (checkedCount === leaves.length) return 'checked';
  return 'indeterminate';
}

export function TeamTree({
  nodes,
  value,
  onChange,
  selectionMode = 'multiple',
  disabled = false,
  emptyMessage = 'No teams yet.',
  className = '',
  'data-testid': testId,
  ...aria
}: TeamTreeProps): JSX.Element {
  const tree = useMemo(() => buildTree(nodes), [nodes]);
  const flat = useMemo(() => flatten(tree), [tree]);
  const rowRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [activeId, setActiveId] = useState<string | null>(null);

  const ariaLabel = aria['aria-label'];
  const activeIndex = Math.max(
    0,
    flat.findIndex((n) => n.id === activeId)
  );

  const moveFocus = (nextIndex: number) => {
    const clamped = Math.min(Math.max(nextIndex, 0), flat.length - 1);
    const target = flat[clamped];
    if (!target) return;
    setActiveId(target.id);
    rowRefs.current[target.id]?.focus();
  };

  const toggle = (node: BuiltNode, checked: boolean) => {
    if (disabled) return;
    if (selectionMode === 'single') {
      onChange(checked ? new Set([node.id]) : new Set());
      return;
    }
    const affected = leafIds(node);
    const next = new Set(value);
    if (checked) affected.forEach((id) => next.add(id));
    else affected.forEach((id) => next.delete(id));
    onChange(next);
  };

  if (flat.length === 0) {
    return <p className={`text-sm text-muted-foreground ${className}`}>{emptyMessage}</p>;
  }

  return (
    <ul role="tree" aria-label={ariaLabel} data-testid={testId} className={`space-y-1 ${className}`}>
      {flat.map((node, index) => {
        const state = selectionMode === 'single' ? (value.has(node.id) ? 'checked' : 'unchecked') : stateOf(node, value);
        const checkboxId = `${ariaLabel.replace(/\s+/g, '-').toLowerCase()}-${node.id}`;
        const isActive = index === activeIndex;
        return (
          <li key={node.id} role="treeitem" aria-level={node.depth + 1} aria-selected={state !== 'unchecked'}>
            <div
              className="flex min-w-0 items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 hover:border-border/60"
              style={{ marginLeft: `${Math.min(node.depth, 6) * 1.25}rem` }}
            >
              <Checkbox
                id={checkboxId}
                ref={(el) => {
                  rowRefs.current[node.id] = el;
                }}
                value={state === 'checked'}
                indeterminate={state === 'indeterminate'}
                disabled={disabled}
                tabIndex={isActive ? 0 : -1}
                onChange={(e) => toggle(node, e.target.checked)}
                onFocus={() => setActiveId(node.id)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    moveFocus(index + 1);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    moveFocus(index - 1);
                  } else if (e.key === 'Home') {
                    e.preventDefault();
                    moveFocus(0);
                  } else if (e.key === 'End') {
                    e.preventDefault();
                    moveFocus(flat.length - 1);
                  }
                }}
              />
              {/* `min-w-0` on the flex child + `truncate` on the text itself
                  (2-137's content rule): a long organization/region/branch
                  name is clipped visually rather than pushing the row into
                  horizontal overflow, and the FULL name stays reachable via
                  `title` (a native tooltip) rather than being lost. */}
              <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer truncate text-sm text-foreground" title={node.name}>
                {node.name}
              </label>
              <span className="shrink-0 text-xs capitalize text-muted-foreground">{node.kind}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
